import { useState, type MouseEvent } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import { useIntl } from 'react-intl'

import { tokens } from '@configuration/theme'
import ChevronDownIcon from '@icons/ChevronDown.icon'
import GlobeIcon from '@icons/Globe.icon'
import { SUPPORTED_LOCALES } from '@i18n/locales.constants'
import { useLocale } from '@i18n/LocaleContext'

const LanguageSwitcher = () => {
  const intl = useIntl()
  const { locale, setLocale } = useLocale()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleSelect = (next: (typeof SUPPORTED_LOCALES)[number]['short']): void => {
    setLocale(next)
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        component='button'
        type='button'
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        data-testid='language-switcher'
        id='language-switcher-button'
        aria-haspopup='menu'
        aria-controls={open ? 'language-switcher-menu' : undefined}
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: 'common.locale.switcher.aria' })}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          border: 0,
          borderRadius: tokens.radiusMd,
          background: 'transparent',
          fontFamily: 'inherit',
          fontSize: 14,
          color: tokens.businessBlack60,
          cursor: 'pointer',
          transition: 'background .3s, color .3s',
          '&:hover': { background: tokens.smokeySilver, color: tokens.businessBlack },
          '&:focus-visible': { outline: `2px solid ${tokens.versatileViolet}`, outlineOffset: 2 },
        }}
      >
        <GlobeIcon />
        <Box component='span' sx={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {locale}
        </Box>
        <ChevronDownIcon />
      </Box>
      <Menu
        id='language-switcher-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ list: { 'aria-labelledby': 'language-switcher-button' } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiList-root': {
            py: 0,
          },
          '& .MuiPaper-root': {
            transform: 'translate(0px, 5px) !important',
          },
        }}
      >
        {SUPPORTED_LOCALES.map((entry) => (
          <MenuItem
            key={entry.short}
            dense={true}
            selected={entry.short === locale}
            onClick={() => handleSelect(entry.short)}
            sx={{
              '&.Mui-selected': {
                color: tokens.versatileViolet,
              },
            }}
          >
            {intl.formatMessage({ id: entry.labelKey })}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default LanguageSwitcher
